<script setup>
import { computed, ref } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import ButtonNext from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSection,
  DropdownItem,
} from 'next/dropdown-menu/base';

const emit = defineEmits(['showCreateAccountModal']);

const { t } = useI18n();
const { accountId, currentAccount } = useAccount();
const currentUser = useMapGetter('getCurrentUser');
const globalConfig = useMapGetter('globalConfig/get');

const userAccounts = useMapGetter('getUserAccounts');

const searchQuery = ref('');

const showAccountSwitcher = computed(
  () => userAccounts.value.length > 1 && currentAccount.value.name
);

const sortedCurrentUserAccounts = computed(() => {
  return [...(currentUser.value.accounts || [])].sort((a, b) =>
    a.name.localeCompare(b.name)
  );
});

const filteredAccounts = computed(() => {
  if (!searchQuery.value.trim()) {
    return sortedCurrentUserAccounts.value;
  }

  const query = searchQuery.value.toLowerCase().trim();
  return sortedCurrentUserAccounts.value.filter(account => {
    const accountName = account.name.toLowerCase();
    const accountRole = account.custom_role_id
      ? account.custom_role.name.toLowerCase()
      : account.role.toLowerCase();

    return accountName.includes(query) || accountRole.includes(query);
  });
});

const onChangeAccount = newId => {
  const accountUrl = `/app/accounts/${newId}/dashboard`;
  window.location.href = accountUrl;
};

const emitNewAccount = () => {
  emit('showCreateAccountModal');
};

const resetSearch = () => {
  searchQuery.value = '';
};
</script>

<template>
  <DropdownContainer>
    <template #trigger="{ toggle, isOpen }">
      <button
        id="sidebar-account-switcher"
        :data-account-id="accountId"
        aria-haspopup="listbox"
        aria-controls="account-options"
        class="flex items-center gap-2 justify-between w-full rounded-md px-2"
        :class="[
          isOpen && 'bg-n-alpha-1',
          showAccountSwitcher
            ? 'hover:bg-n-alpha-1 cursor-pointer'
            : 'cursor-default',
        ]"
        @click="() => showAccountSwitcher && toggle()"
      >
        <span
          class="text-sm font-medium leading-5 text-n-slate-12 truncate"
          aria-live="polite"
        >
          {{ currentAccount.name }}
        </span>

        <span
          v-if="showAccountSwitcher"
          aria-hidden="true"
          class="i-lucide-chevron-down size-4 text-n-slate-10 flex-shrink-0"
        />
      </button>
    </template>
    <DropdownBody v-if="showAccountSwitcher" class="min-w-80 z-50">
      <DropdownSection :title="t('SIDEBAR_ITEMS.SWITCH_ACCOUNT')">
        <div class="px-2 mb-2">
          <div class="relative">
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="t('SIDEBAR_ITEMS.SEARCH_ACCOUNTS')"
              class="w-full pl-3 pr-10 py-2 text-sm rounded-lg bg-n-alpha-2 border border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-teal-8 focus:border-transparent"
              @click.stop
            />
            <button
              v-if="searchQuery"
              class="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-n-slate-10 hover:text-n-slate-12 transition-colors"
              @click.stop="resetSearch"
            >
              <Icon icon="i-lucide-x" class="size-4" />
            </button>
          </div>
        </div>
        <div class="max-h-80 overflow-y-auto px-2">
          <div
            v-if="filteredAccounts.length === 0"
            class="px-2 py-4 text-center text-sm text-n-slate-10"
          >
            {{ t('SIDEBAR_ITEMS.NO_ACCOUNTS_FOUND') }}
          </div>
          <ul v-else class="gap-2 grid reset-base list-none">
            <DropdownItem
              v-for="account in filteredAccounts"
              :id="`account-${account.id}`"
              :key="account.id"
              class="cursor-pointer"
              @click="onChangeAccount(account.id)"
            >
              <template #label>
                <div
                  :for="account.name"
                  class="text-left rtl:text-right flex gap-2 items-center"
                >
                  <span
                    class="text-n-slate-12 max-w-36 truncate min-w-0"
                    :title="account.name"
                  >
                    {{ account.name }}
                  </span>
                  <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
                  <span
                    class="text-n-slate-11 max-w-24 truncate capitalize"
                    :title="account.name"
                  >
                    {{
                      account.custom_role_id
                        ? account.custom_role.name
                        : account.role
                    }}
                  </span>
                </div>
                <Icon
                  v-show="account.id === accountId"
                  icon="i-lucide-check"
                  class="text-n-teal-11 size-5"
                />
              </template>
            </DropdownItem>
          </ul>
        </div>
      </DropdownSection>
      <DropdownItem v-if="globalConfig.createNewAccountFromDashboard">
        <ButtonNext
          color="slate"
          variant="faded"
          class="w-full"
          size="sm"
          @click="emitNewAccount"
        >
          {{ t('CREATE_ACCOUNT.NEW_ACCOUNT') }}
        </ButtonNext>
      </DropdownItem>
    </DropdownBody>
  </DropdownContainer>
</template>
